/**
 * DailyTransferOutView Component
 * Shows the daily outbound transfer with three sections:
 * 1. Finished Products — expected (from receiving yield calc) vs actual (from packed boxes)
 * 2. Rejected Materials — items rejected during production, with reasons
 * 3. Available Remaining Raw Material — unused lots + session remainders (live net on-hand)
 * Each section displays expected vs actual comparison with variance highlighting.
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLanguage } from '../../../i18n/LanguageContext';
import { wsService } from '../../../services/websocket';
import {
  getItemYieldVariance,
  getItemActualYF,
  isYieldVarianceWarning,
  getYieldVarianceColor,
  getActualYFColor,
  formatYieldVariancePct,
} from '../../../hooks/useYieldFactor';
import { useAuth } from '../../../hooks/useAuth';
import toast from 'react-hot-toast';
import {
  Package,
  AlertTriangle,
  Archive,
  RefreshCw,
  CheckCircle,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Printer,
  FileDown,
  Boxes,
  ScanLine,
  Plus,
} from 'lucide-react';
import DatePickerDropdown from '../../../components/DatePickerDropdown';
import {
  dailyTransferOutService,
  DailyTransferOut,
  DailyTransferOutItem,
} from '../../../services/dailyTransferOutService';
import { hteTransferService } from '../../../services/hteTransferService';
import IcsTransferDetailView from './IcsTransferDetailView';
import { ProductionRecordDetailPage } from './ProductionRecordDetailView';
import CategoryTransferTable from './CategoryTransferTable';
import OutputReconciliationSection from './OutputReconciliationSection';
import NewTransferBackModal from './NewTransferBackModal';
import ProductionOutputScanModal from './ProductionOutputScanModal';
import { productionOutputScanService } from '../../../services/productionOutputScanService';
import { inventoryService } from '../../../services/inventoryService';
import { TransferStatusBadge } from '../../../components/TransferStatusBadge';
import { generateLabelPreviewHtml } from '../../../services/labelPrintService';
import LabelPreviewModal from '../../../components/LabelPreviewModal';

interface DailyTransferOutViewProps {
  /** Pre-select a date (YYYY-MM-DD format). Falls back to today. */
  initialDate?: string;
  /** If provided, renders a "Back to List" button at the top. */
  onBack?: () => void;
  /** Open the Ship Out Scan modal for a given transfer number */
  onOpenShipOutScan?: (transferNumber: string) => void;
}

const DailyTransferOutView = ({ initialDate, onBack, onOpenShipOutScan }: DailyTransferOutViewProps) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user?.role === 'A' || user?.role === 'admin';
  const queryClient = useQueryClient();

  // Confirmation modal state
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [showRawTransferModal, setShowRawTransferModal] = useState(false); // WCO-2322 (6C)
  const [showOutputScanModal, setShowOutputScanModal] = useState(false); // WCO-2322 (6B)

  // Collapsible section state — sections with no items default to collapsed
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  // Tab state: 'summary' shows KPI cards + category tables, 'detail' shows IcsTransferDetailView,
  // 'output' (WCO-2677) shows the production-record detail for the day's output(s).
  const [activeTab, setActiveTab] = useState<'summary' | 'detail' | 'output'>('summary');
  // WCO-2597: the Detail tab renders one of the day's transfer-backs (dto.day_transfers). When the day
  // has several, this holds the selected transfer number; null falls back to the first (see render below).
  const [selectedDetailTransfer, setSelectedDetailTransfer] = useState<string | null>(null);
  // WCO-2677: the Output Detail tab renders one of the day's outputs (dto.outputs). When the day has
  // several, this holds the selected output number; null falls back to the first (see render below).
  const [selectedDetailOutput, setSelectedDetailOutput] = useState<string | null>(null);

  // Date navigation — use initialDate prop when provided, otherwise default to today
  const [selectedDate, setSelectedDate] = useState(() => {
    if (initialDate) return initialDate;
    const today = new Date();
    return today.toISOString().slice(0, 10);
  });

  // Fetch daily transfer out for selected date
  const { data: dto, isLoading, isError, refetch } = useQuery<DailyTransferOut>(
    ['daily-transfer-out', selectedDate],
    () => dailyTransferOutService.getDailyTransferOut(selectedDate),
    {
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  // Initialize collapsed sections — collapse empty sections, expand ones with items
  useEffect(() => {
    if (!dto) return;
    const empty = new Set<string>();
    if (!dto.finished_products?.length) empty.add('finished');
    if (!dto.finished_products_not_intact?.length) empty.add('finishedNotIntact');
    if (!dto.rejected_materials?.length) empty.add('rejected');
    if (!dto.remaining_materials?.length) empty.add('remaining');
    if (!(dto.by_products?.length)) empty.add('byproducts');
    setCollapsedSections(empty);
  }, [dto]);

  // Refresh actuals mutation — used ONLY by the manual refresh button, so the
  // toast notification gives the user explicit feedback for their click.
  const refreshMutation = useMutation(
    () => dailyTransferOutService.aggregateActuals(selectedDate),
    {
      onSuccess: () => {
        toast.success(t('dailyTransferOut.refreshSuccess'));
        queryClient.invalidateQueries(['daily-transfer-out', selectedDate]);
      },
      onError: () => {
        toast.error(t('dailyTransferOut.refreshFailed'));
      },
    }
  );

  // WCO-2170: silent recompute used by initial mount, the 30s poller, and the
  // WebSocket event listener. Same server work as refreshMutation, but no
  // toast — automatic refreshes should be invisible to the user.
  const silentRefreshInFlight = useRef(false);
  const silentRefresh = useCallback(async () => {
    if (silentRefreshInFlight.current) return;
    silentRefreshInFlight.current = true;
    try {
      await dailyTransferOutService.aggregateActuals(selectedDate);
      queryClient.invalidateQueries(['daily-transfer-out', selectedDate]);
    } catch {
      // best-effort: next poll tick or event will retry
    } finally {
      silentRefreshInFlight.current = false;
    }
  }, [selectedDate, queryClient]);

  // Aggregate on mount / date change, then poll every 30s to keep numbers fresh.
  // WCO-2170: always trigger an initial silent recompute (even for empty drafts)
  // so a freshly-opened Transfer Back populates without the user having to
  // click the refresh button.
  // WCO-2322: a DAY has no lifecycle status — only outputs and transfers do (each is sent, then
  // warehouse-received via production_summary.is_approved / warehouse_transfer_summary.is_complete). The
  // DTO status column is vestigial and stays 'draft', so the Production Summary is always editable.
  const isComplete = dto?.status === 'complete';
  const isDraft = !!dto && !isComplete;
  const isFinalized = isComplete;
  useEffect(() => {
    if (!dto || isFinalized) return;
    silentRefresh();
    const intervalId = setInterval(() => { silentRefresh(); }, 30_000);
    return () => { clearInterval(intervalId); };
  }, [selectedDate, dto?.status, silentRefresh]);

  // WCO-2597: the Detail tab no longer fetches the single legacy dto.transfer_number (null in the
  // WCO-2322 multi-transfer model). It now renders IcsTransferDetailView per dto.day_transfers entry
  // (see the Detail-tab render block below), letting the child self-fetch each transfer by number.

  // WebSocket: silent refresh when a transfer-out is completed by the poller,
  // when an operator records waste, or when a sub-session is marked completed
  // (waste-tracking is finalized when a sub-session reaches `completed`).
  // Uses silentRefresh so the supervisor isn't spammed with toast notifications
  // from automatic events triggered by other users.
  const handleTransferWs = useCallback(() => {
    silentRefresh();
  }, [silentRefresh]);

  useEffect(() => {
    wsService.connect();
    wsService.on('hte-transfer-updated', handleTransferWs);
    wsService.on('waste-recorded', handleTransferWs);
    wsService.on('production-session-updated', handleTransferWs);
    // WCO-2680: a manager ADDING a session emits 'production-session-created' (not '-updated'), so this
    // view must also listen to it — otherwise adding a feeder to an output doesn't refresh the day's
    // outputs/targets here (or in the open Scan Production Outputs modal, which reads them as props).
    wsService.on('production-session-created', handleTransferWs);
    return () => {
      wsService.off('hte-transfer-updated', handleTransferWs);
      wsService.off('waste-recorded', handleTransferWs);
      wsService.off('production-session-updated', handleTransferWs);
      wsService.off('production-session-created', handleTransferWs);
    };
  }, [handleTransferWs]);

  // Finalize mutation
  const finalizeMutation = useMutation(
    () =>
      dailyTransferOutService.finalizeTransferOut(
        selectedDate,
        user?.username || user?.user_id
      ),
    {
      onSuccess: async () => {
        toast.success(t('dailyTransferOut.finalizeSuccess'));
        queryClient.invalidateQueries(['daily-transfer-out', selectedDate]);

        // Auto-download the transfer PDF after finalization
        if (dto?.transfer_number) {
          try {
            const blob = await hteTransferService.downloadPdf(dto.transfer_number);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${dto.transfer_number || 'transfer'}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
          } catch {
            toast.error(t('dailyTransferOut.pdfDownloadFailed'));
          }
        }
      },
      onError: () => {
        toast.error(t('dailyTransferOut.finalizeFailed'));
      },
    }
  );

  // WCO-2322: the old per-day Submit was retired — the scan modal's Confirm now writes the ICS records and
  // closes the day (draft → pending once all outputs are sent).

  // WCO-2322: the day's OUTPUT numbers (active) + TRANSFER numbers (daily + any on-demand +Transfer, deduped),
  // listed in the header in place of the single transfer number.
  // Outputs come from getOutputsByRange (NOT dto.outputs) so already-SENT outputs still appear, each with its
  // own ship_status (draft → pending → complete) — mirroring the Transfers list.
  const [headerOutputs, setHeaderOutputs] = useState<Array<{ output_number: string; ship_status: string; destination_warehouse: string }>>([]);
  useEffect(() => {
    if (!selectedDate) { setHeaderOutputs([]); return; }
    let cancelled = false;
    const load = () => {
      productionOutputScanService.getOutputsByRange(selectedDate, selectedDate)
        .then((outs) => { if (!cancelled) setHeaderOutputs(outs.map((o) => ({ output_number: o.output_number, ship_status: o.ship_status, destination_warehouse: o.destination_warehouse }))); })
        .catch(() => { if (!cancelled) setHeaderOutputs([]); });
    };
    load();
    // Poll + refetch on focus: an output's received state (production_summary.is_approved → 'Y') is set
    // Oracle-side with no event, so refresh its capsule without a manual reload. (Transfer statuses ride on
    // dto.day_transfers, refreshed by the page's 30s aggregate poller.)
    const id = setInterval(load, 30_000);
    window.addEventListener('focus', load);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener('focus', load); };
  }, [selectedDate]);

  // Resolve a destination warehouse/location CODE → its human-readable name for the header capsules.
  // Reuses the same cached ICS code lists the Transfers page uses (shared react-query keys → no extra
  // fetch cost). Location descriptions take precedence, falling back to the warehouse description.
  const { data: locationCodes = [] } = useQuery(['ics-location-codes'], () => inventoryService.getLocationCodes());
  const { data: warehouseCodes = [] } = useQuery(['ics-warehouse-codes'], () => inventoryService.getWarehouseCodes());
  const destNameByCode = useMemo(() => {
    const m: Record<string, string> = {};
    // Coerce to arrays: a failed/absent fetch (or a differently-shaped mock) leaves these non-iterable.
    const locs = Array.isArray(locationCodes) ? locationCodes : [];
    const whs = Array.isArray(warehouseCodes) ? warehouseCodes : [];
    for (const loc of locs) if (loc.description) m[loc.location_code] = loc.description;
    for (const wh of whs) if (!m[wh.warehouse_code] && wh.description) m[wh.warehouse_code] = wh.description;
    return m;
  }, [locationCodes, warehouseCodes]);

  // Stacked "Output 900486 [status] / Transfer 900462 [status]" list (mobile + desktop header layouts). Each id
  // shows its OWN status via the shared TransferStatusBadge: outputs use ship_status (is_approved-derived),
  // transfers use their workflow status from dto.day_transfers — the same hte_transfer_workflow.status column
  // the Transfers page reads. No day-level fallback.
  const renderHeaderNumbers = (sizeClass: string) => {
    const dayTransfers = dto?.day_transfers || [];
    return (
      <div className="space-y-0.5">
        {headerOutputs.map((o) => (
          <div key={`o-${o.output_number}`} className={`flex items-center gap-2 ${sizeClass} font-bold text-gray-900 dark:text-white`}>
            <span className="inline-block text-right w-[4.5rem] text-[10px] font-semibold uppercase tracking-wide text-gray-400">{t('dailyTransferOut.outputLabel')}</span>
            <span>{o.output_number}</span>
            <TransferStatusBadge status={o.ship_status} small />
            {o.destination_warehouse && (
              <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                → {o.destination_warehouse}{destNameByCode[o.destination_warehouse] ? ` (${destNameByCode[o.destination_warehouse]})` : ''}
              </span>
            )}
          </div>
        ))}
        {dayTransfers.map((tr) => (
          <div key={`t-${tr.transfer_number}`} className={`flex items-center gap-2 ${sizeClass} font-bold text-gray-900 dark:text-white`}>
            <span className="inline-block text-right w-[4.5rem] text-[10px] font-semibold uppercase tracking-wide text-gray-400">{t('dailyTransferOut.transferLabel')}</span>
            <span>{tr.transfer_number}</span>
            <TransferStatusBadge status={tr.status} small />
            {tr.destination_warehouse && (
              <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                → {tr.destination_warehouse}{destNameByCode[tr.destination_warehouse] ? ` (${destNameByCode[tr.destination_warehouse]})` : ''}
              </span>
            )}
          </div>
        ))}
        {headerOutputs.length === 0 && dayTransfers.length === 0 && (
          <div className={`${sizeClass} font-bold text-gray-900 dark:text-white`}>{selectedDate}</div>
        )}
      </div>
    );
  };

  // Print by-product labels via print-service (same path as operator packing)
  const [isPrintingBPLabels, setIsPrintingBPLabels] = useState(false);
  const [showBPLabelPreview, setShowBPLabelPreview] = useState(false);
  const [bpLabelPreviewHtml, setBpLabelPreviewHtml] = useState<string[]>([]);
  // Marketable by-products are printable when has_label is false (something — case or buffer — still needs a label).
  // WCO-2468: printability is independent of the TRANSFER checkbox (excluded), matching FP-NI / RM-NI —
  // an unchecked (excluded) item still gets a label. Don't filter on excluded here.
  const printableMarketableByProducts = (dto?.by_products || []).filter((bp) =>
    bp.category === 'by_product_marketable' && !bp.has_label
  );
  const byProductLabelsNeeded = !!(
    printableMarketableByProducts.length &&
    printableMarketableByProducts.some((bp) => !bp.has_label)
  );
  const [blinkPrintBtn, setBlinkPrintBtn] = useState(false);

  // Toggle exclusion from transfer (works for by-product, remaining material, FP-NI, and rejected rows)
  const handleToggleExclude = async (item: DailyTransferOutItem) => {
    try {
      const nextExcluded = !item.excluded;
      await dailyTransferOutService.toggleByProductExclude(item.id, nextExcluded);
      queryClient.setQueryData<DailyTransferOut | undefined>(
        ['daily-transfer-out', selectedDate],
        (current) => {
          if (!current) return current;

          const toggleItem = (items: DailyTransferOutItem[]) =>
            items.map((entry) =>
              entry.id === item.id
                ? { ...entry, excluded: nextExcluded }
                : entry
            );

          return {
            ...current,
            finished_products: toggleItem(current.finished_products || []),
            remaining_materials: toggleItem(current.remaining_materials || []),
            by_products: toggleItem(current.by_products || []),
            finished_products_not_intact: toggleItem(current.finished_products_not_intact || []),
            rejected_materials: toggleItem(current.rejected_materials || []),
          };
        }
      );
      // Recompute backend-derived fields (raw_material_transfers expected count, outputs) so Select Output
      // reflects the new selection without a manual refresh. (The setQueryData above is just instant checkbox feedback.)
      queryClient.invalidateQueries(['daily-transfer-out', selectedDate]);
    } catch {
      toast.error(t('dailyTransferOut.excludeFailed') || 'Failed to update exclusion');
    }
  };

  const handlePrintByProductLabels = async () => {
    const items = printableMarketableByProducts;
    if (items.length === 0) return;
    setIsPrintingBPLabels(true);
    try {
      const resp = await dailyTransferOutService.createByProductInventory(
        selectedDate,
        items.map(bp => ({ item_id: bp.id })),
        user?.employee_number || user?.id || ''
      );
      const results = resp.results || [];
      if (results.length === 0) {
        toast.success('Labels already generated for these by-products');
        return;
      }
      if (resp.simulate) {
        const previewPages: string[] = [];
        for (const r of results) {
          previewPages.push(await generateLabelPreviewHtml(r.label_data, r.barcode));
        }
        setBpLabelPreviewHtml(previewPages);
        setShowBPLabelPreview(true);
      } else {
        toast.success(`Printed ${results.length} by-product label(s)`);
      }
      refetch();
    } catch (err: any) {
      console.error('By-product label print failed:', err);
      toast.error(err?.message || 'Failed to print by-product labels');
    } finally {
      setIsPrintingBPLabels(false);
    }
  };

  // Not-intact finished products: one label each, printed regardless of checkbox.
  const [isPrintingNiLabels, setIsPrintingNiLabels] = useState(false);
  const [showNiLabelPreview, setShowNiLabelPreview] = useState(false);
  const [niLabelPreviewHtml, setNiLabelPreviewHtml] = useState<string[]>([]);
  const notIntactItems = dto?.finished_products_not_intact || [];
  const printableNotIntact = notIntactItems.filter((it) => !it.has_label);
  const notIntactLabelsNeeded = printableNotIntact.length > 0;

  const handlePrintNiLabels = async () => {
    const items = printableNotIntact;
    if (items.length === 0) return;
    setIsPrintingNiLabels(true);
    try {
      const resp = await dailyTransferOutService.createFinishProductNiLabels(
        selectedDate,
        items.map((it) => ({ item_id: it.id })),
        user?.employee_number || user?.id || ''
      );
      const results = resp.results || [];
      if (results.length === 0) {
        toast.success(t('dailyTransferOut.niLabelsAlreadyPrinted'));
        return;
      }
      if (resp.simulate) {
        const previewPages: string[] = [];
        for (const r of results) {
          previewPages.push(await generateLabelPreviewHtml(r.label_data, r.barcode));
        }
        setNiLabelPreviewHtml(previewPages);
        setShowNiLabelPreview(true);
      } else {
        toast.success(t('dailyTransferOut.niLabelsPrinted').replace('{{count}}', String(results.length)));
      }
      refetch();
    } catch (err: any) {
      console.error('Not-intact label print failed:', err);
      toast.error(err?.message || t('dailyTransferOut.niLabelsPrintFailed'));
    } finally {
      setIsPrintingNiLabels(false);
    }
  };

  // Remainder (not intact) raw material: print labels. Mirrors FP-NI behavior via dedicated
  // ics.hte_lot.label_printed column. Not-intact rows are identified by their notes text.
  const [isPrintingRemainderLabels, setIsPrintingRemainderLabels] = useState(false);
  const [showRemainderLabelPreview, setShowRemainderLabelPreview] = useState(false);
  const [remainderLabelPreviewHtml, setRemainderLabelPreviewHtml] = useState<string[]>([]);
  const remainderNotIntactItems = (dto?.remaining_materials || []).filter(
    (it) => it.notes === 'Remainder (not intact)'
  );
  const printableRemainderNi = remainderNotIntactItems.filter((it) => !it.has_label);
  const remainderNiLabelsNeeded = printableRemainderNi.length > 0;

  const handlePrintRemainderLabels = async () => {
    const items = printableRemainderNi;
    if (items.length === 0) return;
    setIsPrintingRemainderLabels(true);
    try {
      const resp = await dailyTransferOutService.createRemainderNiLabels(
        selectedDate,
        items.map((it) => ({ item_id: it.id })),
        user?.employee_number || user?.id || ''
      );
      const results = resp.results || [];
      if (results.length === 0) {
        toast.success('Labels already printed');
        return;
      }
      if (resp.simulate) {
        const previewPages: string[] = [];
        for (const r of results) {
          previewPages.push(await generateLabelPreviewHtml(r.label_data, r.barcode));
        }
        setRemainderLabelPreviewHtml(previewPages);
        setShowRemainderLabelPreview(true);
      } else {
        toast.success(`Printed ${results.length} remainder label(s)`);
      }
      refetch();
    } catch (err: any) {
      console.error('Remainder label print failed:', err);
      toast.error(err?.message || 'Failed to print remainder labels');
    } finally {
      setIsPrintingRemainderLabels(false);
    }
  };

  // WCO-2623: supervisor Reprint override for an already-printed FP-NI / RM-NI label (misplaced-label
  // case). Opens a confirm-with-required-reason dialog; on confirm, force-reprints that ONE lot,
  // superseding the prior label. Routes by category to the matching endpoint.
  const [reprintTarget, setReprintTarget] = useState<DailyTransferOutItem | null>(null);
  const [reprintReason, setReprintReason] = useState('');
  const [isReprinting, setIsReprinting] = useState(false);
  const handleReprint = (item: DailyTransferOutItem) => { setReprintTarget(item); setReprintReason(''); };

  const confirmReprint = async () => {
    const item = reprintTarget;
    const reason = reprintReason.trim();
    if (!item || !reason) return;
    setIsReprinting(true);
    try {
      const employee = user?.employee_number || user?.id || '';
      const isByproduct = item.category === 'by_product_marketable';
      const isFpNi = item.category === 'finished_product_not_intact';
      // WCO-2633 Phase 2: a by-product reprint targets the specific Cases/Buffer variant of this split row.
      const resp = isByproduct
        ? await dailyTransferOutService.createByProductInventory(
            selectedDate,
            [{ item_id: item.id as number, variant: (item as any)._variant === 'buffer' ? 'buffer' : 'cases' }],
            employee, { force: true, reason })
        : isFpNi
        ? await dailyTransferOutService.createFinishProductNiLabels(
            selectedDate, [{ item_id: item.id }], employee, { force: true, reason })
        : await dailyTransferOutService.createRemainderNiLabels(
            selectedDate, [{ item_id: item.id }], employee, { force: true, reason });
      const results = resp.results || [];
      if (resp.simulate && results.length > 0) {
        const pages: string[] = [];
        for (const r of results) pages.push(await generateLabelPreviewHtml(r.label_data, r.barcode));
        if (isByproduct) { setBpLabelPreviewHtml(pages); setShowBPLabelPreview(true); }
        else if (isFpNi) { setNiLabelPreviewHtml(pages); setShowNiLabelPreview(true); }
        else { setRemainderLabelPreviewHtml(pages); setShowRemainderLabelPreview(true); }
      } else {
        toast.success(t('dailyTransferOut.reprintSuccess'));
      }
      setReprintTarget(null);
      setReprintReason('');
      refetch();
    } catch (err: any) {
      console.error('Reprint failed:', err);
      toast.error(err?.message || t('dailyTransferOut.reprintFailed'));
    } finally {
      setIsReprinting(false);
    }
  };

  // Date navigation helpers
  const goToPreviousDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() - 1);
    setSelectedDate(date.toISOString().slice(0, 10));
  };

  const goToNextDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + 1);
    setSelectedDate(date.toISOString().slice(0, 10));
  };

  const handleFinalize = () => {
    setShowFinalizeModal(true);
  };

  const confirmFinalize = () => {
    setShowFinalizeModal(false);
    finalizeMutation.mutate();
  };

  const handleDownloadPdf = async () => {
    if (!dto?.transfer_number) return;
    try {
      const blob = await hteTransferService.downloadPdf(dto.transfer_number);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${dto.transfer_number || 'transfer'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t('dailyTransferOut.pdfDownloadFailed'));
    }
  };

  const handlePrintPdf = async () => {
    if (!dto?.transfer_number) return;
    try {
      const blob = await hteTransferService.downloadPdf(dto.transfer_number);
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      }
    } catch {
      toast.error(t('dailyTransferOut.pdfDownloadFailed'));
    }
  };

  // Status color helper
  const getRowStatus = (item: DailyTransferOutItem) => {
    if (isFinalized && item.excluded) return 'not-shipped';
    if (isFinalized) return 'finalized';
    const hasActuals = item.actual_quantity != null || item.actual_weight != null;
    if (item.category === 'finished_product') {
      if (!hasActuals) return 'estimate';
      // WCO-2322: all its outputs sent → "Shipped" (reuses the existing 'finalized' label).
      if (item.ship_status === 'shipped') return 'finalized';
      if (isYieldVarianceWarning(item, undefined)) return 'variance-warning';
      return 'completed';
    }
    // Non-finished-product categories — just label by category
    if (item.category === 'finished_product_not_intact') return 'finished-not-intact';
    if (item.category === 'rejected_material') return 'rejected';
    if (item.category === 'by_product_marketable') return 'by-product-marketable';
    if (item.category === 'by_product_production') return 'by-product-production';
    if (item.category === 'by_product') return 'by-product';
    return 'remainder';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'estimate':
        return (
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
            {t('dailyTransferOut.estimate')}
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
            {t('dailyTransferOut.completed')}
          </span>
        );
      case 'finished-not-intact':
        return (
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
            {t('dailyTransferOut.finishedNotIntactBadge')}
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
            {t('dailyTransferOut.rejected')}
          </span>
        );
      case 'remainder':
        return (
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
            {t('dailyTransferOut.remainder')}
          </span>
        );
      case 'by-product':
        return (
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">
            {t('dailyTransferOut.byProduct')}
          </span>
        );
      case 'by-product-marketable':
        return (
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300">
            {t('dailyTransferOut.byProductMarketable')}
          </span>
        );
      case 'by-product-production':
        return (
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300">
            {t('dailyTransferOut.byProductProduction')}
          </span>
        );
      case 'variance-warning':
        return (
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300">
            {t('dailyTransferOut.varianceWarning')}
          </span>
        );
      case 'not-shipped':
        return (
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
            {t('dailyTransferOut.notShipped')}
          </span>
        );
      case 'finalized':
        return (
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
            {t('dailyTransferOut.finalized')}
          </span>
        );
      default:
        return null;
    }
  };

  // Toggle a collapsible section
  const toggleSection = (key: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };


  return (
    <div className="space-y-2 md:space-y-4">
      {/* Tab navigation + action buttons — only show when data is loaded */}
      {dto && (
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            {/* Hide inner back arrow on mobile — the header already has one */}
            {onBack && (
              <button
                onClick={onBack}
                className="hidden md:inline-flex items-center gap-1 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors border-b-2 border-transparent print:hidden"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => setActiveTab('summary')}
              className={`px-2 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'summary'
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              {t('dailyTransferOut.tabSummary')}
            </button>
            <button
              onClick={() => setActiveTab('detail')}
              className={`hidden md:inline-flex px-2 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'detail'
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              {t('dailyTransferOut.tabTransferDetail')}
            </button>
            {/* WCO-2677: Output Detail tab — production-record analog of the Transfer Detail tab. */}
            <button
              onClick={() => setActiveTab('output')}
              className={`hidden md:inline-flex px-2 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'output'
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              {t('dailyTransferOut.tabOutputDetail')}
            </button>
          </div>

          <div className="flex items-center gap-2 print:hidden">
            {isDraft && (
              <button
                onClick={() => refreshMutation.mutate()}
                disabled={refreshMutation.isLoading}
                className="inline-flex items-center p-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw
                  className={`w-4 h-4 ${
                    refreshMutation.isLoading ? 'animate-spin' : ''
                  }`}
                />
              </button>
            )}
            {/* WCO-2322 (Step 2): + New — create an empty Transfer/Output container to scan into */}
            {isDraft && (
              <button
                onClick={() => setShowRawTransferModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">{t('dailyTransferOut.newLabel')}</span>
              </button>
            )}
            {/* TBHTE-383: Print/Download hidden for manager/supervisor, visible for admin only */}
            {isAdmin && dto.transfer_number && (
              <button
                onClick={handlePrintPdf}
                disabled={isDraft}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Printer className="w-4 h-4" />
                {t('warehouseTransfer.print')}
              </button>
            )}
            {isAdmin && dto.transfer_number && (
              <button
                onClick={handleDownloadPdf}
                disabled={isDraft}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileDown className="w-4 h-4" />
                {t('warehouseTransfer.downloadPdf')}
              </button>
            )}
            {/* Scan → opens the per-output scan modal (badge → select → scan → Confirm). Replaces the old
                Submit button: Confirm now writes the ICS records and closes the day. Desktop only. */}
            {isDraft && (
              <button
                onClick={() => {
                  if (byProductLabelsNeeded) {
                    setBlinkPrintBtn(true);
                    setTimeout(() => setBlinkPrintBtn(false), 2000);
                    return;
                  }
                  setShowOutputScanModal(true);
                }}
                className={`hidden md:inline-flex items-center px-3 py-1.5 text-sm text-white rounded-lg ${
                  byProductLabelsNeeded ? 'bg-primary-600 opacity-50 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700'
                }`}
              >
                <ScanLine className="w-4 h-4 mr-1.5" />
                {t('shipOutScan.title')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Transfer No. + status + date — shown below tabs, hidden on the detail/output tabs (WCO-2677) */}
      {dto && activeTab !== 'detail' && activeTab !== 'output' && (
        <div className="flex items-center justify-between gap-3">
          {/* Left: stacked info */}
          <div className="flex-1 min-w-0">
            {onBack ? (
              <>
                {/* Mobile: stacked layout — each id carries its own status capsule (renderHeaderNumbers) */}
                <div className="md:hidden">
                  {renderHeaderNumbers('text-xs')}
                  <div className="flex items-center gap-1.5 mt-1">
                    <Calendar className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {selectedDate}
                      {(dto.actuals_generated_at || dto.expected_generated_at) && (
                        <> {new Date((dto.actuals_generated_at || dto.expected_generated_at)!).toLocaleString([], { hour: 'numeric', minute: '2-digit', hour12: true })}</>
                      )}
                    </span>
                    {dto.finalized_by && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">· {dto.finalized_by}</span>
                    )}
                  </div>
                </div>
                {/* Desktop: stacked numbers (each id carries its own status capsule) + finalized */}
                <div className="hidden md:flex items-start gap-2">
                  {renderHeaderNumbers('text-lg')}
                  {dto.finalized_by && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Finalized by: {dto.finalized_by}
                    </span>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  onClick={goToPreviousDay}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 print:hidden"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <span className="hidden print:inline text-sm font-medium text-gray-900">{selectedDate}</span>
                  <DatePickerDropdown
                    selected={selectedDate || null}
                    onSelect={(date) => setSelectedDate(date)}
                  >
                    {({ onClick }) => (
                      <button type="button" onClick={onClick} className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 print:hidden">
                        {selectedDate ? (() => { const d = new Date(selectedDate + 'T00:00:00'); return `${d.toLocaleDateString('en-US', { weekday: 'short' })} — ${d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`; })() : 'Select date...'}
                      </button>
                    )}
                  </DatePickerDropdown>
                </div>
                <button
                  onClick={goToNextDay}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 print:hidden"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <span
                  className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                    dto.status === 'complete' || dto.status === 'draft'
                      ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                  }`}
                >
                  {dto.status === 'complete'
                    ? t('dailyTransferOut.complete')
                    : dto.status === 'draft'
                      ? 'Draft'
                      : 'Pending'}
                </span>
              </div>
            )}
          </div>

          {/* Desktop: date on the right */}
          {onBack && (
            <div className="hidden md:flex items-center gap-2 print:hidden shrink-0">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {selectedDate}
                {(dto.actuals_generated_at || dto.expected_generated_at) && (
                  <> {new Date((dto.actuals_generated_at || dto.expected_generated_at)!).toLocaleString([], { hour: 'numeric', minute: '2-digit', hour12: true })}</>
                )}
              </span>
            </div>
          )}

          {/* Mobile: square Scan button → the scan modal (Confirm replaces the old Submit). */}
          {isDraft && (
            <button
              onClick={() => {
                if (byProductLabelsNeeded) {
                  setBlinkPrintBtn(true);
                  setTimeout(() => setBlinkPrintBtn(false), 2000);
                  return;
                }
                setShowOutputScanModal(true);
              }}
              className={`md:hidden flex flex-col items-center justify-center gap-0.5 w-12 h-12 rounded-xl text-white transition-colors print:hidden shrink-0 ${
                byProductLabelsNeeded ? 'bg-primary-600 opacity-50 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700 active:bg-primary-800'
              }`}
            >
              <ScanLine className="w-5 h-5" />
              <span className="text-[9px] font-semibold">Scan</span>
            </button>
          )}
        </div>
      )}

      {/* Loading / Error / No Data */}
      {isLoading && (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      )}

      {isError && !isLoading && (
        <div className="text-center py-12 text-gray-400 dark:text-gray-500">
          {t('dailyTransferOut.noDataForDate')}
        </div>
      )}

      {/* Summary tab — KPI cards + category tables */}
      {dto && activeTab === 'summary' && (
        <>
          {/* WCO-2322: unscanned-boxes submit gate (Output Destinations table removed) */}
          <OutputReconciliationSection
            unscannedCount={dto.unscanned_count}
            unscannedBoxes={dto.unscanned_boxes}
          />
          {/* Summary Cards */}
          <div className="hidden md:grid grid-cols-5 gap-4">
            {/* Remaining Materials */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-2.5 md:p-4">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Archive className="w-3.5 h-3.5 md:w-4 md:h-4 text-blue-600 shrink-0" />
                <span className="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('dailyTransferOut.remainingMaterials')}
                </span>
              </div>
              <div className="space-y-0.5 text-[11px] md:text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">Items:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{dto.totals.remaining.count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Weight:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {dto.totals.remaining.total_weight.toFixed(2)} lbs
                  </span>
                </div>
              </div>
            </div>

            {/* Finished Products */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-2.5 md:p-4">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Package className="w-3.5 h-3.5 md:w-4 md:h-4 text-green-600 shrink-0" />
                <span className="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('dailyTransferOut.finishedProducts')}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-x-2 md:gap-x-4 gap-y-0.5 text-[11px] md:text-xs">
                <div>
                  <span className="text-gray-500">{t('endOfDaySummary.expQty')}:</span>{' '}
                  <span className="font-medium text-gray-900 dark:text-white">
                    {dto.totals.finished.expected_quantity.toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">{t('endOfDaySummary.actQty')}:</span>{' '}
                  <span className="font-medium text-gray-900 dark:text-white">
                    {dto.totals.finished.actual_quantity.toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">{t('endOfDaySummary.expWeight')}:</span>{' '}
                  <span className="font-medium text-gray-900 dark:text-white">
                    {dto.totals.finished.expected_weight.toFixed(2)} lbs
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">{t('endOfDaySummary.actWeight')}:</span>{' '}
                  <span className="font-medium text-gray-900 dark:text-white">
                    {dto.totals.finished.actual_weight.toFixed(2)} lbs
                  </span>
                </div>
              </div>
            </div>

            {/* Finished Products not intact */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-2.5 md:p-4">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Package className="w-3.5 h-3.5 md:w-4 md:h-4 text-amber-600 shrink-0" />
                <span className="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('dailyTransferOut.finishedProductsNotIntact')}
                </span>
              </div>
              <div className="space-y-0.5 text-[11px] md:text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">Items:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{dto.totals.finished_not_intact?.count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Weight:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {(dto.totals.finished_not_intact?.total_weight || 0).toFixed(2)} lbs
                  </span>
                </div>
              </div>
            </div>

            {/* Rejected Materials */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-2.5 md:p-4">
              <div className="flex items-center gap-1.5 mb-1.5">
                <AlertTriangle className="w-3.5 h-3.5 md:w-4 md:h-4 text-red-600 shrink-0" />
                <span className="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('dailyTransferOut.rejectedMaterials')}
                </span>
              </div>
              <div className="space-y-0.5 text-[11px] md:text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">Items:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{dto.totals.rejected.count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Weight:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {dto.totals.rejected.total_weight.toFixed(2)} lbs
                  </span>
                </div>
              </div>
            </div>

            {/* By-Products */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-2.5 md:p-4">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Boxes className="w-3.5 h-3.5 md:w-4 md:h-4 text-purple-600 shrink-0" />
                <span className="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('dailyTransferOut.byProducts')}
                </span>
              </div>
              <div className="space-y-0.5 text-[11px] md:text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">Items:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{dto.totals.by_products?.count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Weight:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {(dto.totals.by_products?.total_weight || 0).toFixed(2)} lbs
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Section Tables */}
          <CategoryTransferTable
            sectionKey="finished"
            title={t('dailyTransferOut.finishedProducts')}
            icon={<Package className="w-4 h-4 text-green-600" />}
            items={dto.finished_products}
            showExpected={true}
            isCollapsed={collapsedSections.has('finished')}
            onToggleCollapse={() => toggleSection('finished')}
            onToggleExclude={isDraft ? handleToggleExclude : undefined}
            getRowStatus={getRowStatus}
            getStatusBadge={getStatusBadge}
            getItemYieldVariance={getItemYieldVariance}
            getItemActualYF={getItemActualYF}
            getActualYFColor={getActualYFColor}
            getYieldVarianceColor={getYieldVarianceColor}
            formatYieldVariancePct={formatYieldVariancePct}
          />

          <CategoryTransferTable
            sectionKey="finishedNotIntact"
            onReprint={isDraft ? handleReprint : undefined}
            title={t('dailyTransferOut.finishedProductsNotIntact')}
            icon={<Package className="w-4 h-4 text-amber-600" />}
            items={notIntactItems}
            showExpected={false}
            isCollapsed={collapsedSections.has('finishedNotIntact')}
            onToggleCollapse={() => toggleSection('finishedNotIntact')}
            getRowStatus={getRowStatus}
            getStatusBadge={getStatusBadge}
            getItemYieldVariance={getItemYieldVariance}
            getItemActualYF={getItemActualYF}
            getActualYFColor={getActualYFColor}
            getYieldVarianceColor={getYieldVarianceColor}
            formatYieldVariancePct={formatYieldVariancePct}
            headerAction={
              notIntactItems.length > 0 && isDraft ? (
                <button
                  onClick={(e) => { e.stopPropagation(); handlePrintNiLabels(); }}
                  disabled={isPrintingNiLabels || !notIntactLabelsNeeded}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-md shadow-sm transition-colors disabled:opacity-50 ${
                    notIntactLabelsNeeded ? 'bg-amber-500 hover:bg-amber-400 ring-2 ring-amber-400/50 shadow-amber-500/30 shadow-lg' :
                    'bg-amber-600/70 hover:bg-amber-600'
                  }`}
                >
                  <Printer className="w-3.5 h-3.5" />
                  {isPrintingNiLabels ? 'Printing...' : 'Print Labels'}
                </button>
              ) : undefined
            }
            onToggleExclude={isDraft ? handleToggleExclude : undefined}
          />

          <CategoryTransferTable
            sectionKey="remaining"
            onReprint={isDraft ? handleReprint : undefined}
            title={t('dailyTransferOut.remainingMaterials')}
            icon={<Archive className="w-4 h-4 text-blue-600" />}
            items={dto.remaining_materials}
            showExpected={false}
            isCollapsed={collapsedSections.has('remaining')}
            onToggleCollapse={() => toggleSection('remaining')}
            getRowStatus={getRowStatus}
            getStatusBadge={getStatusBadge}
            getItemYieldVariance={getItemYieldVariance}
            getItemActualYF={getItemActualYF}
            getActualYFColor={getActualYFColor}
            getYieldVarianceColor={getYieldVarianceColor}
            formatYieldVariancePct={formatYieldVariancePct}
            headerAction={
              isDraft && remainderNotIntactItems.length > 0 ? (
                <button
                  onClick={(e) => { e.stopPropagation(); handlePrintRemainderLabels(); }}
                  disabled={isPrintingRemainderLabels || !remainderNiLabelsNeeded}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-md shadow-sm transition-colors disabled:opacity-50 ${
                    remainderNiLabelsNeeded ? 'bg-blue-500 hover:bg-blue-400 ring-2 ring-blue-400/50 shadow-blue-500/30 shadow-lg' :
                    'bg-blue-600/70 hover:bg-blue-600'
                  }`}
                >
                  <Printer className="w-3.5 h-3.5" />
                  {isPrintingRemainderLabels ? 'Printing...' : 'Print Labels'}
                </button>
              ) : undefined
            }
            onToggleExclude={isDraft ? handleToggleExclude : undefined}
          />

          <CategoryTransferTable
            sectionKey="byproducts"
            title={t('dailyTransferOut.byProducts')}
            icon={<Boxes className="w-4 h-4 text-purple-600" />}
            items={dto.by_products || []}
            showExpected={false}
            isCollapsed={collapsedSections.has('byproducts')}
            onToggleCollapse={() => toggleSection('byproducts')}
            onReprint={isDraft ? handleReprint : undefined}
            getRowStatus={getRowStatus}
            getStatusBadge={getStatusBadge}
            getItemYieldVariance={getItemYieldVariance}
            getItemActualYF={getItemActualYF}
            getActualYFColor={getActualYFColor}
            getYieldVarianceColor={getYieldVarianceColor}
            formatYieldVariancePct={formatYieldVariancePct}
            headerAction={
              (dto.by_products?.length ?? 0) > 0 && isDraft ? (
                <button
                  onClick={(e) => { e.stopPropagation(); handlePrintByProductLabels(); }}
                  disabled={isPrintingBPLabels || printableMarketableByProducts.length === 0}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-md shadow-sm transition-colors disabled:opacity-50 ${
                    blinkPrintBtn ? 'animate-bounce bg-purple-400 ring-2 ring-purple-300 ring-offset-1 dark:ring-offset-gray-900' :
                    byProductLabelsNeeded ? 'bg-purple-500 hover:bg-purple-400 ring-2 ring-purple-400/50 shadow-purple-500/30 shadow-lg' :
                    'bg-purple-600/70 hover:bg-purple-600'
                  }`}
                >
                  <Printer className="w-3.5 h-3.5" />
                  {isPrintingBPLabels ? 'Printing...' : 'Print Labels'}
                </button>
              ) : undefined
            }
            onToggleExclude={isDraft ? handleToggleExclude : undefined}
          />

          <CategoryTransferTable
            sectionKey="rejected"
            title={t('dailyTransferOut.rejectedMaterials')}
            icon={<AlertTriangle className="w-4 h-4 text-red-600" />}
            items={dto.rejected_materials}
            showExpected={false}
            isCollapsed={collapsedSections.has('rejected')}
            onToggleCollapse={() => toggleSection('rejected')}
            getRowStatus={getRowStatus}
            getStatusBadge={getStatusBadge}
            getItemYieldVariance={getItemYieldVariance}
            getItemActualYF={getItemActualYF}
            getActualYFColor={getActualYFColor}
            getYieldVarianceColor={getYieldVarianceColor}
            formatYieldVariancePct={formatYieldVariancePct}
            onToggleExclude={isDraft ? handleToggleExclude : undefined}
          />
        </>
      )}

      {/* Detail tab — IcsTransferDetailView per day_transfer (WCO-2597). The header already lists the day's
          transfer-backs from dto.day_transfers; the Detail tab now follows the same multi-transfer model
          instead of the single legacy dto.transfer_number. IcsTransferDetailView self-fetches by number and
          renders its own loading / "Transfer not found" states, so we only handle the empty (no transfers) case. */}
      {dto && activeTab === 'detail' && (() => {
        const detailTransfers = dto.day_transfers ?? [];
        // Selected transfer, falling back to the first when unset or no longer present (e.g. after a date change).
        const activeDetailRow =
          detailTransfers.find((tr) => tr.transfer_number === selectedDetailTransfer)
          ?? detailTransfers[0]
          ?? null;
        const activeDetailTransfer = activeDetailRow?.transfer_number ?? null;

        if (!activeDetailTransfer) {
          return (
            <div className="text-center py-12 text-gray-400">
              {t('dailyTransferOut.noTransferForDay')}
            </div>
          );
        }

        return (
          <>
            {detailTransfers.length > 1 && (
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {t('dailyTransferOut.selectTransfer')}
                </span>
                {detailTransfers.map((tr) => (
                  <button
                    key={tr.transfer_number}
                    type="button"
                    aria-pressed={tr.transfer_number === activeDetailTransfer}
                    onClick={() => setSelectedDetailTransfer(tr.transfer_number)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold border transition-colors ${
                      tr.transfer_number === activeDetailTransfer
                        ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                        : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <span>{tr.transfer_number}</span>
                    <TransferStatusBadge status={tr.status} small />
                  </button>
                ))}
              </div>
            )}
            {/* WCO-2650: a freshly created draft has no ICS document yet (created at submit), so
                IcsTransferDetailView would 404 with "Transfer not found". Show a friendly not-submitted
                message instead — this is what preserves WCO-2597's intent while still listing the draft. */}
            {activeDetailRow?.has_document === false ? (
              <div className="text-center py-12 text-gray-400">
                {t('dailyTransferOut.transferNotSubmittedYet')}
              </div>
            ) : (
              <IcsTransferDetailView
                key={activeDetailTransfer}
                transferNumber={activeDetailTransfer}
                hideActions
              />
            )}
          </>
        );
      })()}

      {/* WCO-2677: Output Detail tab — production-record analog of the Transfer Detail block above.
          Renders ProductionRecordDetailPage for the day's output(s) (dto.outputs). Multi-output days
          get a per-output selector; a document-less output (has_document === false, WCO-2655) shows a
          non-dead-ending message instead of the detail view's "not found" state. */}
      {dto && activeTab === 'output' && (() => {
        // Active outputs only (cancelled outputs have no production record to show).
        const detailOutputs = (dto.outputs ?? []).filter((o) => o.status !== 'cancelled');
        // Selected output, falling back to the first when unset or no longer present (e.g. after a date change).
        const activeOutputRow =
          detailOutputs.find((o) => o.output_number === selectedDetailOutput)
          ?? detailOutputs[0]
          ?? null;
        const activeOutputNumber = activeOutputRow?.output_number ?? null;

        if (!activeOutputNumber) {
          return (
            <div className="text-center py-12 text-gray-400">
              {t('dailyTransferOut.noOutputForDay')}
            </div>
          );
        }

        return (
          <>
            {detailOutputs.length > 1 && (
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {t('dailyTransferOut.selectOutput')}
                </span>
                {detailOutputs.map((o) => (
                  <button
                    key={o.output_number}
                    type="button"
                    aria-pressed={o.output_number === activeOutputNumber}
                    onClick={() => setSelectedDetailOutput(o.output_number)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold border transition-colors ${
                      o.output_number === activeOutputNumber
                        ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                        : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <span>{o.output_number}</span>
                    {o.ship_status && <TransferStatusBadge status={o.ship_status} small />}
                  </button>
                ))}
              </div>
            )}
            {/* WCO-2655: a document-less output (unsent draft — production_summary written only at send)
                would 404 in ProductionRecordDetailPage. Show a friendly "no production record yet"
                message instead, mirroring the transfer-side transferNotSubmittedYet guard. */}
            {activeOutputRow?.has_document === false ? (
              <div className="text-center py-12 text-gray-400">
                {t('dailyTransferOut.outputNoRecordYet')}
              </div>
            ) : (
              <ProductionRecordDetailPage
                key={activeOutputNumber}
                productionNumber={activeOutputNumber}
                initialTab="O"
                embedded
              />
            )}
          </>
        );
      })()}

      {/* WCO-2322 (6B): per-output production scan modal (new; ShipOutScannerModal left intact) */}
      <ProductionOutputScanModal
        isOpen={showOutputScanModal}
        onClose={() => setShowOutputScanModal(false)}
        date={selectedDate}
        outputs={dto?.outputs || []}
        transfers={dto?.raw_material_transfers || []}
        unscannedCount={dto?.unscanned_count ?? 0}
        onChanged={() => queryClient.invalidateQueries(['daily-transfer-out', selectedDate])}
      />

      {/* WCO-2322 (Step 2): + New "New Transfer-Back" — create an empty Transfer/Output container to scan into */}
      <NewTransferBackModal
        isOpen={showRawTransferModal}
        onClose={() => setShowRawTransferModal(false)}
        date={selectedDate}
        createdBy={user?.username || user?.user_id}
        onCreated={() => queryClient.invalidateQueries(['daily-transfer-out', selectedDate])}
      />


      {/* Finalize confirmation modal */}
      {showFinalizeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center print:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowFinalizeModal(false)}
          />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('dailyTransferOut.finalizeTransferOut')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                {t('dailyTransferOut.confirmFinalize')}
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowFinalizeModal(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={confirmFinalize}
                  disabled={finalizeMutation.isLoading}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {t('common.confirm')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <LabelPreviewModal
        isOpen={showBPLabelPreview}
        onClose={() => setShowBPLabelPreview(false)}
        labelHtml={bpLabelPreviewHtml}
        copies={bpLabelPreviewHtml.length || 1}
      />

      <LabelPreviewModal
        isOpen={showNiLabelPreview}
        onClose={() => setShowNiLabelPreview(false)}
        labelHtml={niLabelPreviewHtml}
        copies={niLabelPreviewHtml.length || 1}
      />

      <LabelPreviewModal
        isOpen={showRemainderLabelPreview}
        onClose={() => setShowRemainderLabelPreview(false)}
        labelHtml={remainderLabelPreviewHtml}
        copies={remainderLabelPreviewHtml.length || 1}
      />

      {/* WCO-2623: supervisor Reprint override — confirm + required reason */}
      {reprintTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !isReprinting && setReprintTarget(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
              {t('dailyTransferOut.reprintDialogTitle')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              {t('dailyTransferOut.reprintDialogBody').replace(
                '{{item}}',
                `${reprintTarget.item_number}${reprintTarget.item_name ? ' — ' + reprintTarget.item_name : ''}`
              )}
            </p>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              {t('dailyTransferOut.reprintReasonLabel')} <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reprintReason}
              onChange={(e) => setReprintReason(e.target.value)}
              rows={2}
              autoFocus
              placeholder={t('dailyTransferOut.reprintReasonPlaceholder')}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setReprintTarget(null)}
                disabled={isReprinting}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={confirmReprint}
                disabled={isReprinting || !reprintReason.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-amber-600 text-white hover:bg-amber-500 disabled:opacity-50"
              >
                {isReprinting ? t('dailyTransferOut.reprinting') : t('dailyTransferOut.reprintConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyTransferOutView;
